import Analytics from 'analytics-opencare';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { UserMarketName } from 'connect/saga/handlers/user/user-market-name';
import { ForcedExperimentVariantsStateHandler } from 'connect/state/handlers/experiments';
import { useAnalyticsService } from 'services/AnalyticsService';
import Logger from 'utils/Logger';

import { GetExperimentVariants } from '../../connect/saga/handlers/experiment/get-experiment-variants';
import { AssignedExperiment, Experiment, ExperimentName, VARIANTS } from '../constants/experiment-configs';

const analytics = new Analytics();

const findExperimentVariant = (experimentVariants: AssignedExperiment[], experimentName: ExperimentName) => {
  const matchingExperiments = experimentVariants.filter(
    (variant) => variant.name === experimentName,
  );

  if (matchingExperiments.length <= 0) {
    return null;
  }

  if (matchingExperiments.length > 1) {
    Logger.error(
      `More than one variant found for experiment ${experimentName}`,
    );
  }

  return matchingExperiments[0]!.variant.variantNumber;
};

interface UseExperimentProps {
  experiment: Experiment;
  condition?: boolean;
  skipTracking?: boolean;
}

interface ExperimentResult {
  variant: string;
  isControl: boolean;
  isVariant: boolean;
}

export const useExperiment = ({ experiment, condition = true, skipTracking = false }: UseExperimentProps) => {
  const analyticsService = useAnalyticsService();
  const userMarket = useSelector(UserMarketName.value!.selector);
  const experimentVariants = useSelector(GetExperimentVariants.value!.selector);
  const forcedExperimentVariants = useSelector(
    ForcedExperimentVariantsStateHandler.selector,
  );

  const createResult = ({ variantNumber }: { variantNumber: string; }) => {
    const type = variantNumber === '0' ? VARIANTS.CONTROL : VARIANTS.VARIANT;
    return {
      variant: variantNumber,
      isControl: type === VARIANTS.CONTROL,
      isVariant: type === VARIANTS.VARIANT,
    };
  };

  const [result, setResult] = useState<ExperimentResult | null>(null);

  // Check if the experiment is applicable to the current user
  const doesConditionPass = condition || condition === undefined;
  const doesMarketMatch =
    !experiment.markets
    || experiment.markets.includes(userMarket?.toLowerCase());
  const isExperimentApplicable = doesConditionPass && doesMarketMatch;

  useEffect(() => {
    if (!isExperimentApplicable || !experimentVariants?.length) {
      return;
    }

    const variant =
      forcedExperimentVariants?.[experiment.name]
      || findExperimentVariant(experimentVariants, experiment.name as ExperimentName);

    if (!variant) {
      Logger.error(`No variant found for experiment ${experiment.name}`);
      return;
    }

    // Make sure result is only a new object when the variant changes
    setResult((prev) => {
      if (prev?.variant && prev.variant !== variant) {
        return prev;
      }

      return createResult({
        variantNumber: variant,
      });
    });
  }, [isExperimentApplicable, experiment?.name, experimentVariants, forcedExperimentVariants]);

  // Track in heap when the experiment variant is assigned
  useEffect(() => {
    if (!result?.variant || skipTracking) {
      return;
    }

    analytics.trackPageView({
      pageName: 'experiment-variant-assigned',
      pageType: analytics.pageTypes.EXPERIMENT_VARIANT_ASSIGNED,
      content: {
        no_track_cro: 'true',
        experiment_name: experiment.name,
        experiment_variant: result.variant,
      },
      ...analyticsService.analyticsData,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every time, so we don't want it in the dependency array
  }, [result?.variant, skipTracking, experiment?.name]);

  // Fall back to a control variant
  // This is so that in the event where the experiment is not applicable,
  // or no variant is found, we can still return a result
  // that will have minimal side effects
  return result || createResult({ variantNumber: '0' });
};
